import ActivityGoalSelector from '@UI/activityGoalSelector/ActivityGoalSelector';
import Attachment from '@UI/attachment/Attachment';
import { TextEditor, AddFileModal, AddLinkModal } from '@UI/index';
import commonUtils from '@kathondvla/sri-client/common-utils';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LinkIcon from '@mui/icons-material/Link';
import { Button, IconButton } from '@mui/material';
import { Modal } from '@kathondvla/react-shared-components/src/components';
import './activityChildEditor.scss';
import ModalErrorBoundary from '@UI/errorBoundaries/ModalErrorBoundary';

const ActivityChildEditor = ({
  activity,
  goalOptions,
  curricula,
  renderTo,
  onLeerplanSelection,
  occurencesPerGoal,
  onChange,
  onRemove,
}) => {
  const [showGoals, setShowGoals] = useState(!!activity.goals?.length);
  const [attachments, setAttachments] = useState(activity.attachments ?? []);
  const addFileModalRef = useRef();
  const addLinkModalRef = useRef();
  const activityRef = useRef();

  const onAddAttHandler = (att) => {
    const wrappedAtt = { href: `temp-${commonUtils.generateUUID()}`, $$expanded: att };
    const updatedAtt = [...attachments, wrappedAtt];
    setAttachments(updatedAtt);
    onChange?.({ ...activity, attachments: updatedAtt });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <works>
  const period = useMemo(
    () =>
      activity?.period
        ? `${format(parseISO(activity.period.startDate), 'dd/MM')} - ${format(
            parseISO(activity.period.endDate),
            'dd/MM',
          )}`
        : '05/09 - 10/09',
    [activity?.period.startDate],
  );

  const changeGoals = (goals) => {
    onChange?.({
      ...activity,
      goals,
    });
  };

  const titleChangeHandler = (text) => {
    onChange?.({
      ...activity,
      description: text,
    });
  };

  const removeAttachment = (att) => {
    const updatedAtt = attachments.map((e) => {
      if (e.href === att.href)
        return {
          ...e,
          deleted: true,
        };

      return e;
    });

    setAttachments(updatedAtt);
    onChange?.({ ...activity, attachments: updatedAtt });
  };

  const openFileModalHandler = () => {
    addFileModalRef.current.open({
      onConfirm: onAddAttHandler,
    });
  };

  const openLinkModalHandler = () => {
    addLinkModalRef.current.open({
      onConfirm: onAddAttHandler,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <works>
  useLayoutEffect(() => {
    if (renderTo) {
      activityRef.current?.scrollIntoView({ behaviour: 'smooth', block: 'center' });
    }
  }, []);

  return (
    <div className="activity-child-editor" ref={activityRef}>
      <div className="date-info">
        <span className="dates">{period}</span>
        <div className="buttons">
          <Button startIcon={<DeleteIcon />} onClick={() => onRemove?.(activity)}>
            Verwijderen
          </Button>
          <Button startIcon={<AddBoxIcon />} onClick={() => setShowGoals(!showGoals)}>
            Bijkomende leerplandoelen
          </Button>
        </div>
      </div>

      {showGoals && (
        <div className="goals">
          <ActivityGoalSelector
            goals={activity.goals}
            occurencesPerGoal={occurencesPerGoal}
            options={goalOptions}
            curricula={curricula}
            onChange={changeGoals}
            onLeerplanSelection={(cur) => onLeerplanSelection(activity, cur, 'child')}
          />
        </div>
      )}

      <div className="info">
        <div className="description">
          <header>Inhoud</header>
          <div className="editor">
            <TextEditor value={activity.description} onChange={titleChangeHandler} />
          </div>
        </div>
        <div className="attachments">
          <header>
            <span>Bijlage(n) & links</span>
            <div className="attachment-buttons">
              <Button startIcon={<AttachFileIcon />} onClick={openFileModalHandler}>
                Bijlage
              </Button>
              <Button startIcon={<LinkIcon />} onClick={openLinkModalHandler}>
                Link
              </Button>
            </div>
          </header>
          <div className="list">
            {attachments
              .filter((att) => !att.deleted)
              .map((att) => (
                <div key={att.href} className="attachment-wrapper">
                  <Attachment item={att.$$expanded} />
                  <IconButton color="primary" onClick={() => removeAttachment(att)}>
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}
          </div>
        </div>
      </div>

      <Modal
        ref={addFileModalRef}
        component={<AddFileModal />}
        closeOnClickOutside
        errorBoundaryComponent={ModalErrorBoundary}
      />
      <Modal
        ref={addLinkModalRef}
        component={<AddLinkModal />}
        closeOnClickOutside
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </div>
  );
};

export default ActivityChildEditor;
